'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

type ConnectionState = 'connected' | 'disconnected' | 'connecting';

interface ConnectionMetrics {
  latency: number;
  lastSync: string;
  dataQuality: number;
}

interface ConnectionStatusProps {
  className?: string;
}

const ConnectionStatus = ({ className = '' }: ConnectionStatusProps) => {
  const [connectionState, setConnectionState] = useState<ConnectionState>('connected');
  const [showDetails, setShowDetails] = useState(false);
  const [metrics, setMetrics] = useState<ConnectionMetrics>({
    latency: 45,
    lastSync: new Date().toISOString(),
    dataQuality: 99.8,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics((prev) => ({
        ...prev,
        latency: Math.floor(Math.random() * 100) + 20,
        lastSync: new Date().toISOString(),
        dataQuality: 99 + Math.random(),
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatusConfig = () => {
    switch (connectionState) {
      case 'connected':
        return {
          icon: 'CheckCircleIcon',
          text: 'Connected',
          color: 'text-success',
          bgColor: 'bg-success/10',
          borderColor: 'border-success/20',
          pulseColor: 'bg-success',
        };
      case 'disconnected':
        return {
          icon: 'XCircleIcon',
          text: 'Disconnected',
          color: 'text-error',
          bgColor: 'bg-error/10',
          borderColor: 'border-error/20',
          pulseColor: 'bg-error',
        };
      case 'connecting':
        return {
          icon: 'ArrowPathIcon',
          text: 'Connecting',
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning/20',
          pulseColor: 'bg-warning',
        };
    }
  };

  const config = getStatusConfig();

  const formatTimestamp = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`flex items-center gap-2 px-4 py-2 rounded-md border ${config.bgColor} ${config.borderColor} hover:shadow-md transition-all duration-250 ease-out active:scale-98`}
        aria-label="Connection status"
      >
        <div className="relative">
          <Icon name={config.icon as any} size={20} className={config.color} />
          {connectionState === 'connected' && (
            <span
              className={`absolute -top-0.5 -right-0.5 w-2 h-2 ${config.pulseColor} rounded-full animate-pulse-glow`}
            />
          )}
        </div>
        <span className={`font-mono text-sm font-medium ${config.color} hidden sm:inline`}>
          {config.text}
        </span>
        <Icon
          name={showDetails ? 'ChevronUpIcon' : 'ChevronDownIcon'}
          size={16}
          className={`${config.color} hidden sm:inline`}
        />
      </button>

      {showDetails && (
        <div className="absolute top-full right-0 mt-2 w-72 bg-card border border-border rounded-md shadow-xl z-dropdown overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-secondary">
            <h3 className="font-mono text-sm font-semibold text-foreground">Connection Metrics</h3>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-sans text-sm text-text-secondary">Status</span>
              <span className={`font-mono text-sm font-medium ${config.color}`}>{config.text}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-sans text-sm text-text-secondary">Latency</span>
              <span className="font-data text-sm font-medium text-foreground">{metrics.latency}ms</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-sans text-sm text-text-secondary">Last Sync</span>
              <span className="font-data text-sm font-medium text-foreground">
                {formatTimestamp(metrics.lastSync)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-sans text-sm text-text-secondary">Data Quality</span>
              <span className="font-data text-sm font-medium text-success">
                {metrics.dataQuality.toFixed(1)}%
              </span>
            </div>
          </div>
          <div className="px-4 py-3 border-t border-border bg-secondary">
            <button
              onClick={() => setConnectionState('connecting')}
              className="w-full px-4 py-2 bg-accent text-accent-foreground rounded-md font-mono text-sm font-medium hover:shadow-lg transition-all duration-250 ease-out active:scale-98"
            >
              Reconnect
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionStatus;